import { Controller } from "@hotwired/stimulus";

/**
 * News feed — the one page of a post (29/10/2026), which saves itself: two
 * seconds after the last change — a word, a box, a block of the audience —
 * the whole page is sent as a draft and its state is shown (« Saving… »,
 * « Draft saved », « Could not save »).
 *
 * « Aperçu » saves at once, then reloads the card in its Turbo Frame. The
 * importance switches the bar: an urgent post is not scheduled, it goes out.
 */
export default class extends Controller {
  static targets = ["status", "kind", "event", "previewFrame", "normalOnly", "urgentOnly"];
  static values = { autosaveUrl: String, previewUrl: String, saving: String, saved: String, failed: String };

  disconnect() {
    clearTimeout(this.timer);
  }

  changed(event) {
    // The schedule date belongs to « Programmer », not to the draft.
    if (!this.autosaveUrlValue || event?.target?.name === "scheduled_at") return;
    clearTimeout(this.timer);
    this.timer = setTimeout(() => this.save(), 2000);
  }

  // A button of the bar sends the page: the pending save is not needed.
  submitted() {
    clearTimeout(this.timer);
  }

  // Enter in a field never presses « Programmer », the first button of the bar.
  blockEnter(event) {
    if (event.key === "Enter" && event.target.tagName === "INPUT") event.preventDefault();
  }

  kindChanged(event) {
    this.kindTargets.forEach((label) => label.classList.toggle("is-selected", label.contains(event.target)));
    if (this.hasEventTarget) this.eventTarget.hidden = event.target.value !== "event";
  }

  importanceChanged(event) {
    const urgent = event.target.value === "urgent";
    this.normalOnlyTargets.forEach((element) => { element.hidden = urgent; });
    this.urgentOnlyTargets.forEach((element) => { element.hidden = !urgent; });
  }

  async preview(event) {
    if (!event.target.closest('[data-tab="preview"]') || !this.hasPreviewFrameTarget) return;
    clearTimeout(this.timer);
    await this.save();
    const frame = this.previewFrameTarget;
    if (frame.getAttribute("src") === this.previewUrlValue) {
      frame.reload();
    } else {
      frame.src = this.previewUrlValue;
    }
  }

  async save() {
    this.show(this.savingValue);
    const body = new FormData(this.element);
    body.set("_method", "patch");
    body.delete("intent");
    try {
      const response = await fetch(this.autosaveUrlValue, {
        method: "POST",
        headers: { Accept: "application/json", "X-CSRF-Token": this.csrf },
        body,
        credentials: "same-origin",
      });
      if (response.ok) {
        this.show(this.savedValue);
      } else {
        // Refused (a draft without a title): the reason, in a sentence.
        const data = await response.json().catch(() => ({}));
        this.show(data.errors?.[0] || this.failedValue);
      }
    } catch (_) {
      this.show(this.failedValue);
    }
  }

  show(text) {
    if (this.hasStatusTarget) this.statusTarget.textContent = text;
  }

  get csrf() {
    return document.querySelector('meta[name="csrf-token"]')?.content || "";
  }
}
